import { Card, CardBody, Button } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from 'date-fns';
import { useLocale } from "~/hooks/useLocale";
import { ReadOnlyRating } from "../ReadOnlyRating";
import { LikeButton } from "../LikeButton";
import { ReviewCost } from '~/components/atoms/ReviewCost';
import { ShareButton } from '~/components/atoms/ShareButton';
import { PlaceCardSmall } from '../PlaceCardSmall';
import { BaseLink } from '~/components/atoms/Link';
import { ROUTES } from '~/constants/routes.contants';
import { FiChevronRight, FiMessageSquare } from 'react-icons/fi';
import { FoodCardSmall } from "../FoodCardSmall";
import { MediaDisplay } from '../MediaDisplay';
import { ReviewResponseDto } from "@bitecritic-api/src/reviews/dto/review.response.dto";
import { MdTranslate } from "react-icons/md";
import { dateLocales } from "~/locales";
import { UserAvatar } from "~/components/atoms/UserAvatar";
import { CountryFlag } from "~/components/atoms/CountryFlag";
import { useRef } from "react";
import { useCommentsModal } from "~/components/contexts/CommentsModalContext";
import { generateReviewSlug } from '~/utils/slug-utils';

interface ReviewListItemProps {
  review: ReviewResponseDto;
  showPlaceCard?: boolean;
  showUserCard?: boolean;
  showFoodCard?: boolean;
  isPriority?: boolean;
  onItemClick?: () => void;
}

export const ReviewListItem: React.FC<ReviewListItemProps> = ({
  review,
  showPlaceCard = true,
  showUserCard = true,
  showFoodCard = true,
  isPriority = false,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const { currentLocale, navigateTo } = useLocale();
  const cardRef = useRef<HTMLDivElement>(null);
  const { openCommentsModal } = useCommentsModal();

  const { user } = review;

  const handleProfileClick = () => {
    navigateTo(`${ROUTES.USERS}/${user.username}`);
  };

  const handleCardClick = () => {
    if (onItemClick) {
      onItemClick();
    }
  };
  
  // Check if translations are available for current locale
  const hasTranslation = review.translations &&
    Object.keys(review.translations).length > 0 &&
    review.translations[currentLocale.language] &&
    review.languageCode !== currentLocale.language;

  // Always use translation if available, otherwise use original
  const reviewText = hasTranslation && review.translations?.[currentLocale.language]?.reviewText
    ? review.translations?.[currentLocale.language]?.reviewText
    : review.reviewText;

  const reviewUrl = `${window.location.origin}${ROUTES.REVIEWS}/${review.id}`;
  const reviewSlug = generateReviewSlug({
    id: review.id,
    food: review.food ? { name: review.food.name } : undefined,
    place: review.place ? { name: review.place.name } : undefined,
    reviewText: review.reviewText,
  });
  const reviewPageUrl = `${ROUTES.REVIEWS}/${reviewSlug}`;

  return (
    <Card
      ref={cardRef}
      className='mt-4 bg-content1 shadow-md border border-secondary-100 cursor-pointer hover:shadow-lg transition-shadow'
      onPress={handleCardClick}
    >
      <CardBody className="space-y-4">
        {/* Rating and Cost */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="text-sm text-secondary-800">
              {review.visitedAt && formatDistanceToNow(new Date(review.visitedAt), {
                addSuffix: true,
                locale: dateLocales[currentLocale.language as keyof typeof dateLocales]
              })}
            </div>
            <ReviewCost cost={review.cost ?? 0} countryCode={review.place?.country?.code} className="ml-2" />
          </div>
          <div className="flex items-center gap-2">
            <ReadOnlyRating rating={review.overallRating ?? 0} />
          </div>
        </div>
        {/* User Info */}
        {showUserCard && review.user && (
          <div className="flex items-center gap-4" data-user-card>
            <UserAvatar
              user={user}
              size="lg"
              className="cursor-pointer"
              onClick={handleProfileClick}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3
                  className="text-lg font-semibold cursor-pointer hover:text-warning-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProfileClick();
                  }}
                >
                  {user.username}
                </h3>
                {user.countryCode && (
                  <CountryFlag
                    countryCode={user.countryCode}
                    size="sm"
                  />
                )}
              </div>
              <p className="text-secondary-500">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>
        )}


        {/* Review Media */}
        {review.images && review.images.length > 0 && (
          <MediaDisplay
            media={review.images}
            title={`${review.food?.name || ''} ${review.place?.name || ''}`}
            contextId={`review_${review.id}`}
            isPriority={isPriority}
            className="w-full"
          />
        )}


        {/* Review Text */}
        {reviewText && (
          <div className="pb-4 relative">
            <div
              className="max-h-[20vh] min-h-[110px] overflow-hidden relative"
              style={{
                maskImage: 'linear-gradient(to bottom, black 60%, transparent 80%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 80%)'
              }}
            >
              <p className="text-foreground">{reviewText}</p>
            </div>
            {/* Show translation notice if we're displaying a translation */}
            {hasTranslation && (
              <div className="text-xs text-secondary-400 mb-2 flex items-center">
                <MdTranslate className="mr-1" />
                {t('reviews.translatedFrom', { language: t(`languages.${review.languageCode}`) })}
              </div>
            )}
            <BaseLink
              variant="secondary"
              className="absolute bottom-4 right-0 p-1 rounded-md bg-warning-50 text-warning-700 hover:bg-warning-100"
              href={reviewPageUrl}
            >
              {t('reviews.readFull')}
              <FiChevronRight className='ml-1' />
            </BaseLink>
          </div>
        )}

        {showFoodCard && review.food && (
          <div data-food-card>
            <FoodCardSmall
              food={review.food}
            />
          </div>
        )}
        <div className="flex flex-col gap-2">
          {/* Place Info */}
          {showPlaceCard && review.place && (
            <div data-place-card>
              <PlaceCardSmall
                place={review.place}
              />
            </div>
          )}
        </div>
        {/* Review Actions */}
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {review.id && (
              <>
                <LikeButton
                  reviewId={review.id}
                  initialLikeCount={review.likeCount ?? 0}
                  size="md"
                />
                <Button
                  isIconOnly
                  size="md"
                  variant="light"
                  aria-label="View comments"
                  onPress={() => openCommentsModal(review.id)}
                >
                  <div className="flex items-center gap-1">
                    <FiMessageSquare size={16} />
                    <span className="text-md">{review.commentCount || '0'}</span>
                  </div>
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ShareButton
              title={review.food?.name || review.place?.name || 'Review'}
              text={review.reviewText || ''}
              url={reviewUrl}
            />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}; 